import dayjs from 'dayjs';
import { Button, NumberInput } from '@mantine/core';
import { useMemo, useState } from 'react';
import { IUserAbsences } from '../../../hooks/promises/getUserAbsences';
import { getSaveTitle, validateNumberRange } from './helper';

const daysInMonth = dayjs().daysInMonth();

interface INumberOfAbsencesEditingProps {
  userAbsences: IUserAbsences | null;
  addUserAbsences: (daysOfAbsence: number) => Promise<void>;
  setEditing: React.Dispatch<React.SetStateAction<boolean>>;
  setSaving: React.Dispatch<React.SetStateAction<boolean>>;
  refetch: () => void;
  thisMonthYear: string;
}

function NumberOfAbsencesEditing({
  setEditing,
  addUserAbsences,
  userAbsences,
  setSaving,
  refetch,
  thisMonthYear,
}: INumberOfAbsencesEditingProps) {
  const [inputNumberOfAbsences, setInputNumberOfAbsences] = useState(
    userAbsences?.daysOfAbsence || 0
  );
  const [outOfRangeErr, setOutOfRangeErr] = useState('');

  const numberHasChanged = useMemo(() => {
    return userAbsences?.daysOfAbsence !== inputNumberOfAbsences;
  }, [userAbsences, inputNumberOfAbsences]);

  const saveTitle = useMemo(() => {
    return getSaveTitle(numberHasChanged, outOfRangeErr);
  }, [numberHasChanged, outOfRangeErr]);

  const onChange = (value: number | undefined) => {
    if (value !== undefined) {
      if (!validateNumberRange(value, 0, daysInMonth)) {
        setOutOfRangeErr(`Please input a number between 0 - ${daysInMonth}`);
      }
      if (validateNumberRange(value, 0, daysInMonth)) {
        setOutOfRangeErr('');
        setInputNumberOfAbsences(value);
      }
    }
  };

  const onClickSave = () => {
    setSaving(true);
    addUserAbsences(inputNumberOfAbsences)
      .then(() => {
        refetch();
      })
      .then(() => {
        setSaving(false);
      });
    setEditing(false);
  };

  return (
    <div className="flex flex-col items-end gap-4">
      <NumberInput
        className="w-full"
        label={`${thisMonthYear}: Number of Absences`}
        size="lg"
        onChange={onChange}
        value={inputNumberOfAbsences}
        placeholder="Enter the number of absences for the current month"
        precision={1}
        step={0.5}
        error={outOfRangeErr}
      />
      <div className="flex gap-2">
        <div
          title={saveTitle}
          className={` ${
            !numberHasChanged || outOfRangeErr
              ? 'cursor-not-allowed'
              : 'cursor-pointer'
          } `}
        >
          <Button
            className="font-semibold text-white border-2 bg-primary-500 hover:bg-primary-600 hover:border-primary-600 border-primary-500"
            onClick={onClickSave}
            disabled={!numberHasChanged || Boolean(outOfRangeErr)}
          >
            Save
          </Button>
        </div>
        <Button
          className="bg-red-600 hover:bg-red-700"
          onClick={() => setEditing(false)}
          title="Cancel editing"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

export default NumberOfAbsencesEditing;
